import { Dispatch } from "redux";
import { uuid } from "uuidv4";
import { Element } from "../models/elementModel";
import { AppState } from "../reducers/rootReducer";
import {
  ADD_ELEMENT,
  ADD_ELEMENT_LIST,
  AppActions,
  COPY_ELEMENT,
  COPY_TABLE_CELL_ELEMENTS,
  DROP_ELEMENT,
  EDIT_ELEMENT,
  MOVE_ELEMENT,
  MOVE_ELEMENT_SECTION,
  REMOVE_ALL_ELEMENTS,
  REMOVE_ELEMENT,
  REMOVE_ELEMENT_LIST_BY_FORM_ID,
  SET_ELEMENT,
} from "../types/actions";
import { addTab } from "./tabListAction";
import elementService from "../services/elementService";
import tabService from "../services/tabService";
import produce from "immer";
import { TableRowModel } from "../models/tableRowModel";
import { TabelCell } from "../models/tabelCellModel";
import { TabelHeading } from "../models/tabelHeading";

// add element to redux store

export const addElement = (element: Element): AppActions => ({
  type: ADD_ELEMENT,
  element,
});

export const addElements = (elements: Element[]): AppActions => ({
  type: ADD_ELEMENT_LIST,
  elements,
});

// remove element from redux store

export const removeElement = (id: String): AppActions => ({
  type: REMOVE_ELEMENT,
  id,
});

export const removeElementListByFormId = (formId: String): AppActions => ({
  type: REMOVE_ELEMENT_LIST_BY_FORM_ID,
  formId,
});

// edit element in redux store

export const editElement = (element: Element): AppActions => ({
  type: EDIT_ELEMENT,
  element,
});

// get all elements in redux store

export const setElement = (elements: Element[]): AppActions => ({
  type: SET_ELEMENT,
  elements,
});

export const moveElement = (
  elementDrag: Element,
  elementDrop: Element
): AppActions => ({
  type: MOVE_ELEMENT,
  elementDrag,
  elementDrop,
});

export const moveElementSection = (
  elementDrag: Element,
  elementDrop: Element
): AppActions => ({
  type: MOVE_ELEMENT_SECTION,
  elementDrag,
  elementDrop,
});

export const dropElement = (
  element: Element,
  newSectionId: String
): AppActions => ({
  type: DROP_ELEMENT,
  element,
  newSectionId,
});

export const copyElement = (
  element: Element,
  originalElement: Element
): AppActions => ({
  type: COPY_ELEMENT,
  element,
  originalElement,
});

export const copyTableCellElements = (elements: Element[]): AppActions => ({
  type: COPY_TABLE_CELL_ELEMENTS,
  elements,
});

export const removeAllElements = (elements: Element[]): AppActions => ({
  type: REMOVE_ALL_ELEMENTS,
  elements,
});

// start adding element to redux store

export const startAddElement = (
  elementData: {
    sectionId: String;
    name: String;
    cellId: String;
    isSection: Boolean;
    actions: any;
    formId: String;
    type: String;
    colClass: string;
    sortKey: number;
    properties: any;
    styles: any;
    createdTime: string;
    lastUpdatedTime: string;
  },
  tabsCount: number,
  tabs?: any[]
) => {
  return async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    const {
      sectionId = "",
      name = "",
      formId = "",
      cellId = "",
      isSection = true,
      actions = [],
      type = "",
      colClass = "col-xs-12",
      sortKey = 0,
      properties = {},
      styles = {},
      createdTime = "",
      lastUpdatedTime = "",
    } = elementData;

    const element = {
      sectionId,
      name,
      cellId,
      isSection,
      actions,
      formId,
      type,
      colClass,
      sortKey,
      properties,
      styles,
      createdTime,
      lastUpdatedTime,
    };
    const id = uuid();

    if (element.type === "rdioBtn" || element.type === "chkBox") {
      const groupCode = element.sectionId.split("-");
      element.properties = {
        ...element.properties,
        groupName: `${element.properties.groupName}${groupCode[4]}`,
      };
    }

    if (element.type === "table") {
      let rowList: TableRowModel[] = [];
      let headingList: TabelHeading[] = [];
      let cellList: TabelCell[] = [];
      for (let index = 0; index < tabsCount; index++) {
        const headings = { id: uuid(), name: "Heading" };
        headingList.push(headings);
        const cell = {
          id: uuid(),
          class: "",
          properties: { width: "", height: "", displayInline: false },
        };
        cellList.push(cell);
      }
      const row = {
        id: uuid(),
        class: "",
        cells: cellList,
      };
      rowList.push(row);
      const newElement = produce(element, (draft) => {
        draft.properties.headings = headingList;
        draft.properties.rows = rowList;
      });
      const { data } = await elementService.createElement({
        id,
        ...newElement,
      });

      dispatch(addElement(data.data));

      const templateElementLabel = getState().elementTemplateList.filter(
        (temp) => {
          return temp.type === "lbl";
        }
      );

      for (let index = 0; index < cellList.length; index++) {
        const cell = cellList[index];
        const cellElement: Element = {
          id: uuid(),
          name: `Table_${templateElementLabel[0].name}_${index + 1}`,
          sectionId: element.sectionId,
          actions: element.actions,
          formId: element.formId,
          cellId: cell.id,
          isSection: false,
          type: templateElementLabel[0].type,
          colClass: "col-12",
          sortKey: index + 1,
          properties: produce(templateElementLabel[0].properties, () => {}),
          styles: produce(templateElementLabel[0].styles, () => {}),
          createdTime: "",
          lastUpdatedTime: "",
        };
        console.log(cell);
        const { data } = await elementService.createElement(cellElement);
        dispatch(addElement(data.data));
      }
    } else {
      const { data } = await elementService.createElement({
        id,
        ...element,
      });

      dispatch(addElement(data.data));

      if (element.type === "collaps") {
        for (let index = 0; index < tabsCount; index++) {
          let tabId = uuid();
          const { data } = await tabService.createTab({
            id: tabId,
            elementId: id,
            formId: formId,
            name: "Collapsible",
            properties: {},
            styles: {},
            isDropDownTab: false,
            isMain: true,
            pairedKey: "",
            tabGroupTitle: "",
            display: true,
            createdTime: "",
            lastUpdatedTime: "",
          });
          dispatch(addTab(data.data));
        }
      }

      if (element.type === "tab" && tabs) {
        for (let index = 0; index < tabs?.length; index++) {
          let tabId = uuid();
          const { data } = await tabService.createTab({
            id: tabId,
            elementId: id,
            formId: formId,
            name: "Tab",
            properties: {},
            styles: {},
            isDropDownTab: tabs[index].isDropdown,
            isMain: true,
            pairedKey: tabs[index].isDropdown ? uuid() : "",
            tabGroupTitle: tabs[index].isDropdown ? "Dropdown Tab" : "",
            display: true,
            createdTime: "",
            lastUpdatedTime: "",
          });
          dispatch(addTab(data.data));
        }
      }
    }
  };
};

// start editing element in redux store

export const startEditElement = (element: Element) => {
  return (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    elementService.updateElement(element);
    dispatch(editElement(element));
  };
};

// start removing element in redux store

export const startRemoveElement = (id: String) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(removeElement(id));
  };
};

// start select all elements in redux store

export const startSetElement = (elements: Element[]) => {
  return (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    dispatch(setElement(elements));
  };
};

export const startMoveElement = (
  elementDrag: Element,
  elementDrop: Element
) => {
  return (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    dispatch(moveElement(elementDrag, elementDrop));
    const elements = getState().elementList;
    elementService.moveElements(elements.present);
  };
};

export const startMoveElementSection = (
  elementDrag: Element,
  elementDrop: Element
) => {
  return (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    dispatch(moveElementSection(elementDrag, elementDrop));
    const elements = getState().elementList;
    elementService.moveElements(elements.present);
  };
};

export const startDropEleemnt = (element: Element, newSectionId: String) => {
  return (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    dispatch(dropElement(element, newSectionId));
    const elements = getState().elementList;
    elementService.moveElements(elements.present);
  };
};

export const startCopyElement = (
  elementData: {
    sectionId: String;
    name: String;
    cellId: String;
    isSection: Boolean;
    actions: any;
    formId: String;
    type: String;
    colClass: string;
    sortKey: number;
    properties: any;
    styles: any;
    createdTime: string;
    lastUpdatedTime: string;
  },
  orginalElement: Element
) => {
  return async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    const {
      sectionId = "",
      name = "",
      cellId = "",
      isSection = true,
      actions = [],
      formId = "",
      type = "",
      colClass = "col-xs-12",
      sortKey = 0,
      properties = {},
      styles = {},
      createdTime = "",
      lastUpdatedTime = "",
    } = elementData;

    const element = {
      sectionId,
      name,
      cellId,
      isSection,
      actions,
      formId,
      type,
      colClass,
      sortKey,
      properties,
      styles,
      createdTime,
      lastUpdatedTime,
    };
    const id = uuid();
    const { data } = await elementService.createElement({
      id,
      ...element,
    });
    dispatch(copyElement(data.data, orginalElement));
    const elements = getState().elementList;
    elementService.moveElements(elements.present);
  };
};

export const startAddCell = (element: Element) => {
  return async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    const heading = { id: uuid(), name: "Heading" };
    let cellList: TabelCell[] = [];
    const newElement = produce(element, (draft) => {
      draft.properties.headings.push(heading);
      for (let index = 0; index < draft.properties.rows.length; index++) {
        const cell = {
          id: uuid(),
          class: "",
          properties: { width: "", height: "", displayInline: false },
        };
        draft.properties.rows[index].cells.push(cell);
        cellList.push(cell);
      }
    });
    const { data } = await elementService.updateElement(newElement);
    dispatch(editElement(newElement));

    const templateElementLabel = getState().elementTemplateList.filter(
      (temp) => {
        return temp.type === "lbl";
      }
    );

    for (let index = 0; index < cellList.length; index++) {
      const cell = cellList[index];
      const cellElement: Element = {
        id: uuid(),
        name: templateElementLabel[0].name,
        sectionId: element.sectionId,
        formId: element.formId,
        cellId: cell.id,
        isSection: false,
        actions: element.actions,
        type: templateElementLabel[0].type,
        colClass: "col-12",
        sortKey: 0,
        properties: produce(templateElementLabel[0].properties, () => {}),
        styles: produce(templateElementLabel[0].styles, () => {}),
        createdTime: "",
        lastUpdatedTime: "",
      };
      console.log(cell);
      const { data } = await elementService.createElement(cellElement);
      dispatch(addElement(data.data));
    }
  };
};

export const addRow = (
  element: Element,
  row: TableRowModel,
  index: number,
  isPreview: boolean
) => {
  return async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    let cellElementList: Element[] = [];
    const newRow = produce(row, (draft) => {
      draft.id = uuid();
      for (let index = 0; index < draft.cells.length; index++) {
        let id = uuid();
        // let cellElements = produce(getState().elementList.present.filter((element) => { return element.cellId === draft.cells[index].id}), draft => {
        //   for (let index = 0; index < draft.length; index++) {
        //     draft[index].id = uuid();
        //     draft[index].cellId = id;
        //   }
        // });
        draft.cells[index].id = id;
        // cellElementList = cellElementList.concat(cellElements);
      }
    });

    const newElement = produce(element, (draft) => {
      draft.properties.rows.splice(index + 1, 0, newRow);
    });
    if (!isPreview) {
      const { data } = await elementService.updateElement(newElement);
    }
    dispatch(editElement(newElement));

    for (let index = 0; index < cellElementList.length; index++) {
      if (!isPreview) {
        const { data } = await elementService.createElement(
          cellElementList[index]
        );
        dispatch(addElement(data.data));
      } else {
        dispatch(addElement(cellElementList[index]));
      }
    }
  };
};

export const startCopyRow = (
  element: Element,
  row: TableRowModel,
  index: number,
  isPreview: boolean
) => {
  return async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    let cellElementList: Element[] = [];
    const newRow = produce(row, (draft) => {
      draft.id = uuid();
      for (let index = 0; index < draft.cells.length; index++) {
        let id = uuid();
        let cellElements = produce(
          getState().elementList.present.filter((element) => {
            return element.cellId === draft.cells[index].id;
          }),
          (draft) => {
            for (let index = 0; index < draft.length; index++) {
              draft[index].id = uuid();
              draft[index].cellId = id;
            }
          }
        );
        draft.cells[index].id = id;
        cellElementList = cellElementList.concat(cellElements);
      }
    });

    const newElement = produce(element, (draft) => {
      draft.properties.rows.splice(index + 1, 0, newRow);
    });
    if (!isPreview) {
      const { data } = await elementService.updateElement(newElement);
    }
    dispatch(editElement(newElement));

    for (let index = 0; index < cellElementList.length; index++) {
      if (!isPreview) {
        const { data } = await elementService.createElement(
          cellElementList[index]
        );
        dispatch(addElement(data.data));
      } else {
        dispatch(addElement(cellElementList[index]));
      }
    }
  };
};
